import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '~/lib/auth-context'

interface TermsAcceptanceScreenProps {
  currentVersion: string
  locale: string
  marketingUrl: string
  onAccept: (opts: { lifecycleEmailOptOut: boolean }) => void
  isSubmitting: boolean
  submitError: boolean
}

/**
 * B-new-900 — full-page terms/privacy acceptance screen.
 *
 * Shown (via TermsAcceptanceGate) before any app content on every
 * authenticated route once `getTermsStatus.needsAcceptance` is true. Covers
 * both never-accepted accounts and stale-version re-acceptance.
 */
export function TermsAcceptanceScreen({
  currentVersion,
  locale,
  marketingUrl,
  onAccept,
  isSubmitting,
  submitError,
}: TermsAcceptanceScreenProps) {
  const { t } = useTranslation()
  const { signOut } = useAuth()
  const [termsTicked, setTermsTicked] = useState(false)
  // Reuses B-new-870's opt-out semantics: unticked by default, ticking means
  // "don't send me the four Tier 2 lifecycle emails".
  const [lifecycleEmailOptOut, setLifecycleEmailOptOut] = useState(false)

  // gl -> es for the marketing site, same pattern used elsewhere on this
  // domain (Galician marketing copy isn't published separately).
  const marketingLocale = locale === 'gl' ? 'es' : locale

  return (
    <main
      id="main-content"
      role="main"
      data-testid="terms-acceptance-screen"
      className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4 py-12"
      data-terms-version={currentVersion}
    >
      <div className="w-full max-w-md border-2 border-[#00f0ff]/30 bg-[#0f0f15]/95 p-8 rounded-lg">
        <h1 className="text-2xl font-black mb-4">
          {t('auth.termsAcceptance.heading', 'Before you continue')}
        </h1>

        <label className="flex items-start gap-2 text-sm font-mono text-[#a0a0b0] mb-4">
          <input
            type="checkbox"
            id="terms-acceptance-checkbox"
            checked={termsTicked}
            onChange={(e) => setTermsTicked(e.target.checked)}
            disabled={isSubmitting}
            className="mt-1 w-4 h-4 accent-[#00f0ff]"
          />
          {/* eu-compliance-reviewer C1 fix: NO hardcoded {' '} between
              segments — every locale's prefix/middle/suffix string embeds
              its own leading/trailing whitespace (or deliberately omits it,
              e.g. IT/FR "l'"/"l'" elision before a vowel-initial noun), so
              article/preposition agreement with each locale's actual
              translated noun (gender, number, elision) is correct without
              the component forcing an English-shaped " word " gap. */}
          <span>
            {t('auth.termsAcceptance.checkboxPrefix', 'I accept the ')}
            <a
              href={`${marketingUrl}/${marketingLocale}/terms-of-service/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00f0ff] hover:text-[#39ff14] underline"
            >
              {t('auth.termsOfService', 'Terms of Service')}
            </a>
            {t('auth.termsAcceptance.checkboxMiddle', ' and have read the ')}
            <a
              href={`${marketingUrl}/${marketingLocale}/privacy-policy/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00f0ff] hover:text-[#39ff14] underline"
            >
              {t('auth.privacyPolicy', 'Privacy Policy')}
            </a>
            {t('auth.termsAcceptance.checkboxSuffix', '')}
          </span>
        </label>

        <p className="text-xs font-mono text-[#a0a0b0] mb-2" data-testid="lifecycle-email-notice">
          {t(
            'auth.lifecycleEmailNotice',
            "We'll send a few emails about using your account — activation tips, low-credit alerts and reminders if you've been away. You can opt out below, or manage them any time from the link in each email.",
          )}
        </p>

        <label className="flex items-start gap-2 text-xs font-mono text-[#a0a0b0] mb-6">
          <input
            type="checkbox"
            id="terms-acceptance-lifecycle-optout"
            checked={lifecycleEmailOptOut}
            onChange={(e) => setLifecycleEmailOptOut(e.target.checked)}
            disabled={isSubmitting}
            className="mt-1 w-4 h-4 accent-[#ff00ff]"
          />
          <span>{t('auth.lifecycleEmailOptOutLabel', "Don't send me these account emails")}</span>
        </label>

        {submitError && (
          <div
            role="alert"
            data-testid="terms-acceptance-error"
            className="mb-4 p-3 bg-[#ff00ff]/10 border border-[#ff00ff]/30 text-[#ff00ff] rounded-lg text-sm font-mono"
          >
            {t('auth.termsAcceptance.saveError', "Couldn't save — try again.")}
          </div>
        )}

        <button
          type="button"
          disabled={!termsTicked || isSubmitting}
          onClick={() => onAccept({ lifecycleEmailOptOut })}
          className="w-full py-3 bg-[#00f0ff] hover:bg-[#39ff14] text-[#0a0a0f] font-mono font-bold text-sm transition-all border-none disabled:opacity-50 disabled:cursor-not-allowed rounded-lg mb-4"
        >
          {isSubmitting
            ? t('auth.termsAcceptance.saving', 'SAVING...')
            : t('auth.termsAcceptance.continue', 'Continue')}
        </button>

        <button
          type="button"
          onClick={() => signOut()}
          className="w-full text-center text-xs font-mono text-[#a0a0b0] underline hover:text-white"
        >
          {t('auth.signOutLink', 'Sign out')}
        </button>
      </div>
    </main>
  )
}
